import axios from 'axios';

// Determine the appropriate API URL based on the environment
const getBaseUrl = () => {
  // If REACT_APP_API_URL is set via environment variable, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In production (when served from the same domain as the API)
  if (process.env.NODE_ENV === 'production') {
    // Use relative URL which will be served from the same domain
    return '/api';
  }

  // Default for local development
  return 'http://localhost:5001/api';
};

const API_BASE_URL = getBaseUrl();
const DEBUG_API = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_API === 'true';

if (DEBUG_API) {
  console.log(`[API] Using base URL: ${API_BASE_URL}`);
}

let dynamoPreviewEnabled = false;
let confirmedRole = null;
let onRoleMismatch = null;

function setClientRole(role) {
  confirmedRole = role == null ? null : String(role || '');
}

function getClientRole() {
  if (confirmedRole == null) return '';
  return confirmedRole;
}

function setRoleMismatchHandler(handler) {
  onRoleMismatch = typeof handler === 'function' ? handler : null;
}

function setDynamoPreviewEnabled(_on) {
  dynamoPreviewEnabled = false;
}

function isDynamoPreviewEnabled() {
  return dynamoPreviewEnabled;
}

function isServiceClientRole(role = getClientRole()) {
  return role === 'service_admin' || role === 'service_viewer';
}

function canQuerySamsara(role = getClientRole()) {
  return role === 'admin' || role === 'company_admin';
}

function normalizeApiPath(url = '') {
  let path = String(url || '').split('?')[0];
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      path = new URL(path).pathname;
    } catch (_) {
      /* keep path */
    }
  }
  if (path.startsWith('/api/')) path = path.slice(4);
  else if (path.startsWith('api/')) path = path.slice(3);
  if (!path.startsWith('/')) path = `/${path}`;
  return path;
}

function isSamsaraApiPath(path) {
  return path === '/samsara' || path.startsWith('/samsara/');
}

/** Mirror server serviceRoleMayPass for relative /api paths. */
function serviceClientMayRequest(method, path) {
  const m = String(method || 'GET').toUpperCase();
  if (path.startsWith('/auth')) return true;
  if (path.startsWith('/service-tickets')) return true;
  if (m === 'POST' && path === '/audit/usage') return true;
  if ((m === 'GET' || m === 'PATCH') && path === '/users/profile') return true;
  if (m === 'GET' && (path === '/trucks' || /^\/trucks\/[a-f0-9]{24}$/i.test(path))) return true;
  return false;
}

function skippedResponse(config, data = {}) {
  return Promise.resolve({
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  });
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for authentication
});

apiClient.interceptors.request.use((config) => {
  config.headers['X-Request-Id'] = Math.random().toString(36).slice(2, 10);
  const path = normalizeApiPath(config.url);
  const method = config.method || 'get';
  const role = getClientRole();

  // Fail closed until AuthContext confirms the session role.
  if (isSamsaraApiPath(path) && !canQuerySamsara(role)) {
    config.adapter = () => skippedResponse(config, { connections: [], data: [], skipped: true });
  } else if (isServiceClientRole(role) && !serviceClientMayRequest(method, path)) {
    config.adapter = () => skippedResponse(config, { data: [], skipped: true, serviceOnly: true });
  }

  if (DEBUG_API) {
    console.log('[API][req]', {
      method: config.method,
      url: `${config.baseURL}${config.url}`,
      params: config.params,
    });
  }
  return config;
}, (error) => {
  if (DEBUG_API) {
    console.error('[API][req][ERROR]', error);
  }
  return Promise.reject(error);
});

// Add token to headers if available on initial load
const token = localStorage.getItem('authToken');
if (token) {
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

apiClient.interceptors.response.use(
  (response) => {
    if (DEBUG_API) {
      console.log('[API][res]', {
        status: response.status,
        url: `${response.config.baseURL}${response.config.url}`,
      });
    }
    return response;
  },
  (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;
    const isNetwork = !status && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
    // Skip noisy network blips (common when local API restarts); keep real HTTP failures
    if (!isNetwork) {
      console.error('[API]', status || 'error', `${config.method || '?'} ${(config.baseURL || '')}${config.url || ''}`, message);
    }

    if (status === 403 && /Service accounts can only use tickets/i.test(String(message || ''))) {
      try { onRoleMismatch?.(); } catch (_) { /* ignore */ }
    }

    if (status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      localStorage.removeItem('sessionMetadata');
      delete apiClient.defaults.headers.common['Authorization'];
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?sessionExpired=true';
      }
    }
    return Promise.reject(error);
  }
);

export {
  setDynamoPreviewEnabled,
  isDynamoPreviewEnabled,
  setClientRole,
  getClientRole,
  setRoleMismatchHandler,
  canQuerySamsara,
  isServiceClientRole,
};
export default apiClient;
